import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, PiggyBank, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import adminService from '@services/adminService';
import type { AIBudget } from '@/types/admin';
import { BUDGET_SCOPE_LABELS } from '@/types/admin';
import { formatDateTimeBR, parseMoney } from '@/utils/formatters';
import { BudgetBar } from './BudgetBar';

interface Row {
  scope: string;
  limit: string;
  alert: string;
  hardBlock: boolean;
}

const toRow = (b: AIBudget): Row => ({
  scope: b.scope,
  limit: b.monthly_limit_usd === null ? '' : String(b.monthly_limit_usd).replace('.', ','),
  alert: String(b.alert_percent),
  hardBlock: b.hard_block,
});

/** Admin → IA → Orçamento: teto mensal em US$ (total e por propósito), alerta e bloqueio. */
export const AIBudgetView: React.FC = () => {
  const [budgets, setBudgets] = useState<AIBudget[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const apply = (list: AIBudget[]) => {
    setBudgets(list);
    setRows(list.map(toRow));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      apply(await adminService.listBudgets());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      apply(
        await adminService.saveBudgets(
          rows.map((r) => ({
            scope: r.scope,
            monthly_limit_usd: parseMoney(r.limit),
            alert_percent: Number.parseInt(r.alert, 10) || 0,
            hard_block: r.hardBlock,
          })),
        ),
      );
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h3 flex items-center gap-2">
          <PiggyBank size={20} className="text-slate-500" /> IA — Orçamento
        </h1>
        <p className="text-small text-slate-500 mt-1">
          Teto mensal de gasto com IA do Marketing (separado do orçamento de Vendas/CRM). O mês vai do dia 1 ao último dia,
          no horário de Brasília. O teto total vale para tudo; os tetos por propósito limitam só aquele uso.
        </p>
      </div>

      <section className="card p-4 space-y-4">
        {budgets.map((b) => (
          <BudgetBar key={b.scope} budget={b} />
        ))}
      </section>

      <section className="card p-4 space-y-3">
        <h2 className="text-h4">Tetos</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="table-header">
              <tr>
                <th>Escopo</th>
                <th>Teto mensal (US$)</th>
                <th>Alertar em (%)</th>
                <th>Bloquear ao atingir 100%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.scope} className="table-row">
                  <td className="table-cell font-medium">{BUDGET_SCOPE_LABELS[r.scope] ?? r.scope}</td>
                  <td className="table-cell">
                    <input className="input-field w-32" inputMode="decimal" placeholder="sem teto" value={r.limit} onChange={(e) => set(i, { limit: e.target.value })} />
                  </td>
                  <td className="table-cell">
                    <input className="input-field w-20" inputMode="numeric" value={r.alert} onChange={(e) => set(i, { alert: e.target.value })} />
                  </td>
                  <td className="table-cell">
                    <input type="checkbox" className="checkbox-field" checked={r.hardBlock} onChange={(e) => set(i, { hardBlock: e.target.checked })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {budgets.some((b) => b.updated_at) && (
          <p className="text-[11px] text-slate-400">
            Última alteração:{' '}
            {budgets
              .filter((b) => b.updated_at)
              .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''))
              .slice(0, 1)
              .map((b) => `${formatDateTimeBR(b.updated_at)} por ${b.updated_by}`)}
          </p>
        )}
        <Button size="sm" icon={<Save />} loading={saving} onClick={save}>
          Salvar orçamento
        </Button>
      </section>
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
